import useChangeEvent from '@dyl/hooks/useChangeEvent';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel, FormHelperText, MenuItem, TextField } from '@mui/material';
import { property } from 'lodash';
import React, { useCallback } from 'react';
import { useSettings } from './SettingsManager';

/** @typedef {import('./SettingsManager').Settings} Settings */

export default function SettingsModal({ onClose, ...other }) {
    return (
        <Dialog onClose={onClose} {...other}>
            <DialogTitle>Preferences</DialogTitle>
            <DialogContent>
                <Select
                    field='startupPath'
                    label='Home page'
                    helperText={
                        <>
                            The page to load when you go directly to{' '}
                            <a
                                href={window.location.origin}
                                target='_blank'
                                rel='noreferrer'
                                style={{ textDecorationColor: 'inherit', color: 'inherit' }}
                            >
                                {window.location.host}
                            </a>
                            .
                        </>
                    }
                >
                    <MenuItem value={`/level_select`}>Level Select</MenuItem>
                    <MenuItem value={`/{level_label}/tests`}>Tests</MenuItem>
                    <MenuItem value={`/{level_label}/components`}>Components</MenuItem>
                    <MenuItem value={`/{level_label}/tasks`}>Tasks</MenuItem>
                    <MenuItem value={`/{level_label}/medias`}>Image and Audio files</MenuItem>
                    <MenuItem value={`/{level_label}/rubrics`}>Rubrics</MenuItem>
                    <MenuItem value={`/{level_label}/amtt`}>Automarker Target Templates</MenuItem>
                    <MenuItem value={`/{level_label}/passwords`}>Passwords</MenuItem>
                </Select>

                <Divider style={{ marginBottom: 8 }} />

                <Select
                    field='shouldOpenSidebarAtStartup'
                    label='Open sidebar at startup?'
                >
                    <MenuItem value='always'>Always</MenuItem>
                    <MenuItem value='only-larger-screens'>Only on larger screen sizes</MenuItem>
                    <MenuItem value='never'>Never</MenuItem>
                </Select>

                <Boolean
                    field='shouldAutoHideSidebarOnScreenShrink'
                    label='Auto-hide sidebar when the window becomes too thin'
                />

                <Divider style={{ marginBottom: 8 }} />

                <Boolean
                    field='shouldShowImagePreviewsByDefault'
                    label='Show image list previews by default'
                    helperText='Loading previews may be slow, and increases internet usage.'
                />

                <DialogContentText style={{ marginTop: 16, marginBottom: 0 }}>
                    Settings will be saved and synced across tabs/windows automatically.
                </DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color='primary'>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * @template {keyof Settings} K
 * @param {K} field
 * @returns {[Settings[K], (next: Settings[K]) => void]}
 */
function useField(field) {
    const {
        update,
        [field]: value,
    } = useSettings();

    const setValue = useCallback(next => {
        update({ [field]: next });
    }, [update, field]);

    return [value, setValue];
}

const extractChecked = property(['target', 'checked']);

/**
 * @param {Object} props
 * @param {keyof Settings} props.field
 * @param {string} props.label
 * @param {string} [props.helperText]
 */
function Boolean({ field, label, helperText }) {
    const [value, setValue] = useField(field);
    const onChange = useChangeEvent(setValue, { extract: extractChecked });

    return (
        <FormControl>
            <FormControlLabel
                label={label}
                control={
                    <Checkbox
                        checked={value}
                        onChange={onChange}
                        color='primary'
                    />
                }
            />
            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    );
}

/**
 * @param {{ field: (keyof Settings) } & Omit<import('@mui/material').TextFieldProps, 'value' | 'onChange'>} props
 */
function Select({ field, label, helperText, ...other }) {
    const [value, setValue] = useField(field);
    const onChange = useChangeEvent(setValue);

    return (
        <FormControl>
            <FormControlLabel
                label={label}
                labelPlacement='start'
                control={
                    <TextField
                        select
                        value={value}
                        onChange={onChange}
                        {...other}
                    />
                }
            />
            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    );
}